import { useCallback } from 'react';
import { useHistory } from 'react-router';

import useTriggeredRequest from '@api/hooks/useTriggeredRequest';
import AccountClientRequests from '@api/requests';
import CurrentUserInterface from '@api/requests/auth/interfaces/CurrentUserInterface.interface';

import { showToast } from '@app/providers/ToastProvider';
import { getApplicationsList } from '@app/redux/applications/applications.selectors';
import { getCurrentUser } from '@app/redux/auth/auth.selectors';
import { getBranchEnvironment } from '@app/redux/branchEnvironment/branchEnvironment.selectors';
import { useAppSelector } from '@app/redux/hooks';
import RoutePaths from '@app/routes/paths';
import useGenerateRoutePath from '@app/routes/paths/useGenerateRoutePath';

import useFetchApplicationForm from '@hooks/useFetchApplicationForm';

import { getBranchLabel } from '@utils/branchLabel';
import { isAllowApplicationCreation } from '@utils/environment';
import { env } from '@utils/environment';

const checkBranchApplicationLimit = (branchId: number, applicationCount: number) => {
	if (env.TOP_LEVEL_DOMAIN === 'IN') return branchId === 232 && applicationCount >= 1;
	else return false;
};

export interface Form {
	id: number;
	label: string;
	branch_id: number;
	uma_branch_id: number;
}

function useCreateApplication() {
	const history = useHistory();
	const generateRoutePath = useGenerateRoutePath();

	const branchEnvironment = useAppSelector(getBranchEnvironment);
	const currentUser = useAppSelector(getCurrentUser) as CurrentUserInterface;
	const applications = useAppSelector(getApplicationsList);

	const { startRequest: createApplication, isPending: isLoadingCreateApplication } = useTriggeredRequest(
		AccountClientRequests.Applications.createApplication,
	);

	const { fetchApplicationForm, isLoading: isLoadingApplicationForm } = useFetchApplicationForm();
	const forms: Form[] = [];
	const addedIds: number[] = [];

	currentUser.branches.forEach(branch => {
		if (branch.uma_id === branchEnvironment.branch_id) {
			branch.forms.forEach(form => {
				if (!addedIds.includes(form.id)) {
					addedIds.push(form.id);
					forms.push({
						id: form.id,
						label: getBranchLabel(form.translations[0].data),
						branch_id: branch.id,
						uma_branch_id: branch.uma_id,
					});
				}
			});
		}
	});

	const handleCreateApplication = useCallback(
		async (form: Form) => {
			try {
				const res = await createApplication({
					uma_branch_id: form.uma_branch_id,
					uma_user_id: currentUser.id,
					uma_initiator_id: branchEnvironment.rep_id,
					form_id: form.id,
				});

				await fetchApplicationForm({
					applicationId: res.application.id,
					formId: res.application.form_id,
					formVersion: res.application.form_version,
				});

				history.push(generateRoutePath(RoutePaths.MAIN.APPLICATION_FORM));
			} catch {
				showToast.error('Something went wrong!');
			}
		},
		[createApplication, currentUser.id, fetchApplicationForm, generateRoutePath, history],
	);

	return {
		disabled: !isAllowApplicationCreation() || isLoadingCreateApplication || isLoadingApplicationForm,
		isHidden: checkBranchApplicationLimit(branchEnvironment.branch_id, applications.length),
		loading: isLoadingCreateApplication || isLoadingApplicationForm,
		handleCreateApplication,
		forms,
	};
}

export default useCreateApplication;
